import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { RentalContract, ContractProduct } from "shared/types/rental-contracts";
import { UniversalTable } from "shared/components/UniversalTable";
import { FilterBar } from "shared/components/filterBar";
import { TablePagination } from "components/TablePagination";
import { useIndexationData, PAGE_SIZE } from "./hooks/useIndexationData";
import type { KpiFilter } from "./hooks/useIndexationData";
import {
  INDEXATION_COLUMNS_CONFIG,
  INDEXATION_FILTER_SECTIONS,
  INDEXATION_GROUPBY_OPTIONS,
} from "./config/indexationTableConfig";
import { createIndexationCells } from "./config/indexationCellFactory";
import EllaAvatar from "assets/ella.png";
import {
  getCache,
  setCache,
  safeActiveTab,
  updateContractInCache,
  removeContractFromCache,
} from "./hooks/indexationCache";
import type { IndexationActiveTab } from "./hooks/indexationCache";
import { deriveBasePrice, deriveCurrentPrice } from "./utils/deriveProductPrices";
import { IndexationCalculationModal } from "./components/IndexationCalculationModal";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { FilterSection, FilterChip } from "shared/components/filterBar/types";
import { IndexationKPIBar } from "./components/IndexationKPIBar";

// ─── Constants ────────────────────────────────────────────────────────────────

const BUILDING_FILTER_KEY = "building";
const TIME_RANGE_FILTER_KEY = "timeRange";
const INDEXATION_STATUS_FILTER_KEY = "indexationStatus";

// ─── Main page ────────────────────────────────────────────────────────────────

export const IndexatiesPage = () => {
  const navigate = useNavigate();
  const { session } = useSession();
  const sessionRef = useRef(session);
  sessionRef.current = session;

  const cachedState = getCache();

  // ─── Filter state ───────────────────────────────────────────────────────────
  const [searchText, setSearchText] = useState(cachedState?.filters?.searchText ?? "");
  const [buildingFilters, setBuildingFilters] = useState<string[]>(
    cachedState?.filters?.buildingIds ?? [],
  );
  const [timeRangeFilter, setTimeRangeFilter] = useState<string[]>(
    cachedState?.filters?.timeRangeFilter ?? [],
  );
  const [indexationStatusFilter, setIndexationStatusFilter] = useState<string[]>(
    cachedState?.filters?.indexationStatusFilter ?? [],
  );
  const [groupBy, setGroupBy] = useState<string>(cachedState?.groupBy ?? "none");

  // ─── Tab + pagination + kpiFilter state ────────────────────────────────────
  const [activeTab, setActiveTab] = useState<IndexationActiveTab>(
    safeActiveTab(cachedState?.activeTab),
  );
  const [page, setPage] = useState(cachedState?.currentPage ?? 1);
  const [kpiFilter, setKpiFilter] = useState<KpiFilter | null>(null);

  // ─── Health index months (non-critical) ────────────────────────────────────
  const [availableHealthIndexMonths, setAvailableHealthIndexMonths] = useState<
    Array<{ year: number; month: number }>
  >([]);

  useEffect(() => {
    if (!session) return;
    fetchWithAuth(`${API_URL}/rental/health-index/available-months`, { method: "GET" }, session)
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setAvailableHealthIndexMonths(data))
      .catch((err) => {
        console.error("[IndexatiesPage] Fout bij ophalen gezondheidsindexmaanden:", err);
      });
  }, [session]);

  // ─── Persist cache ──────────────────────────────────────────────────────────
  useEffect(() => { setCache({ groupBy }); }, [groupBy]);
  useEffect(() => { setCache({ activeTab }); }, [activeTab]);
  useEffect(() => { setCache({ currentPage: page }); }, [page]);

  // ─── Reset page to 1 on any filter / tab / kpiFilter change ────────────────
  useEffect(() => {
    setPage(1);
  }, [
    activeTab,
    searchText,
    buildingFilters.join(","),
    timeRangeFilter.join(","),
    indexationStatusFilter.join(","),
    kpiFilter,
  ]);

  // ─── Data fetching ──────────────────────────────────────────────────────────
  const { contracts, total, counts, kpis, duplicateUnits, isLoading, setContracts } =
    useIndexationData({
      page,
      indexationGroup: activeTab,
      searchText,
      buildingIds: buildingFilters,
      timeRangeFilter,
      indexationStatusFilter,
      kpiFilter,
    });

  // ─── KPI sub-filter toggle (toggle same = clear) ───────────────────────────
  const handleKpiFilterToggle = useCallback((filter: KpiFilter) => {
    setKpiFilter((prev) => (prev === filter ? null : filter));
  }, []);

  // ─── Optimistic update helpers ──────────────────────────────────────────────
  const handleSignDateAdded = useCallback(
    (contractId: number, date: string, newMilestoneId?: number) => {
      setContracts((prev) =>
        prev.map((contract) => {
          if (contract.id !== contractId) return contract;
          const existing = contract.contractMilestoneDates || [];
          const filtered = existing.filter((m) => m.milestoneDateType !== "Ondertekeningsdatum");
          filtered.push({
            id: newMilestoneId,
            milestoneDateType: "Ondertekeningsdatum" as any,
            date: new Date(date),
            showMilestoneInOverview: "Ja" as any,
            milestoneArchivedFromOverview: false,
            milestoneArchivedFromOverviewByUserId: null,
            origin: "Gebruiker" as any,
            customDescription: null,
            rentalContractId: contractId,
            userId: "",
            createdAt: new Date(),
            updatedAt: new Date(),
          });
          return { ...contract, contractMilestoneDates: filtered };
        }),
      );
    },
    [setContracts],
  );

  const handleStartDateAdded = useCallback(
    (contractId: number, date: string, newMilestoneId?: number) => {
      setContracts((prev) =>
        prev.map((contract) => {
          if (contract.id !== contractId) return contract;
          const existing = contract.contractMilestoneDates || [];
          const filtered = existing.filter((m) => m.milestoneDateType !== "Startdatum");
          filtered.push({
            id: newMilestoneId,
            milestoneDateType: "Startdatum" as any,
            date: new Date(date),
            showMilestoneInOverview: "Ja" as any,
            milestoneArchivedFromOverview: false,
            milestoneArchivedFromOverviewByUserId: null,
            origin: "Gebruiker" as any,
            customDescription: null,
            rentalContractId: contractId,
            userId: "",
            createdAt: new Date(),
            updatedAt: new Date(),
          });
          return { ...contract, contractMilestoneDates: filtered };
        }),
      );
    },
    [setContracts],
  );

  const handleIndexationDateAdded = useCallback(
    (contractId: number, date: string) => {
      setContracts((prev) =>
        prev.map((contract) =>
          contract.id === contractId
            ? { ...contract, lastIndexationDate: new Date(date) as any }
            : contract,
        ),
      );
    },
    [setContracts],
  );

  // ─── Inline price edit (basishuurprijs / huidige huurprijs) ─────────────────
  // The price-edit modals return the updated rental product; recompute the
  // derived base + current price for that row and update it in place so the
  // list reflects the change without a full reload. Also sync the shared cache
  // so the calculate page uses fresh data.
  const handleProductPriceUpdated = useCallback(
    (contractId: number, updatedProduct: ContractProduct) => {
      const newBasePrice = deriveBasePrice(updatedProduct);
      const newCurrentPrice = deriveCurrentPrice(updatedProduct);

      setContracts((prev) =>
        prev.map((contract) => {
          if (contract.id !== contractId) return contract;
          return {
            ...contract,
            initPrice: newBasePrice,
            activePrice: {
              odooProductId: contract.activePrice?.odooProductId ?? 0,
              equalsInOdoo: contract.activePrice?.equalsInOdoo ?? false,
              price: newCurrentPrice,
            },
            products: contract.products?.map((p) =>
              p.id === updatedProduct.id ? updatedProduct : p,
            ),
          };
        }),
      );

      updateContractInCache(contractId, {
        initPrice: newBasePrice,
        activePrice: {
          odooProductId: 0,
          equalsInOdoo: false,
          price: newCurrentPrice,
        },
      });
    },
    [setContracts],
  );

  // ─── Indexation calculation modal (replaces new-tab navigation) ─────────────
  const [calcContract, setCalcContract] = useState<RentalContract | null>(null);
  const [isCalcModalOpen, setIsCalcModalOpen] = useState(false);

  const handleOpenCalculation = useCallback((contract: RentalContract) => {
    setCalcContract(contract);
    setIsCalcModalOpen(true);
  }, []);

  const handleCloseCalculation = useCallback(() => {
    setIsCalcModalOpen(false);
    setCalcContract(null);
  }, []);

  // After an indexation is applied, drop the row from the current list without a
  // full reload so the user keeps their scroll position, and sync the cache.
  const handleIndexationApplied = useCallback(
    (contractId: number) => {
      setContracts((prev) => prev.filter((c) => c.id !== contractId));
      removeContractFromCache(contractId);
    },
    [setContracts],
  );

  // ─── Row renderer ───────────────────────────────────────────────────────────
  const renderRow = useCallback(
    (contract: RentalContract, context: any) =>
      createIndexationCells(contract, {
        ...context,
        navigate,
        onSignDateAdded: handleSignDateAdded,
        onStartDateAdded: handleStartDateAdded,
        onIndexationDateAdded: handleIndexationDateAdded,
        onProductPriceUpdated: handleProductPriceUpdated,
        onOpenCalculation: handleOpenCalculation,
        availableHealthIndexMonths,
      }),
    [
      navigate,
      handleSignDateAdded,
      handleStartDateAdded,
      handleIndexationDateAdded,
      handleProductPriceUpdated,
      handleOpenCalculation,
      availableHealthIndexMonths,
    ],
  );

  // ─── FilterBar sections (dynamic building options from current page) ────────
  const filterBarSections: FilterSection[] = useMemo(() => {
    const buildings = new Map<string, { key: string; label: string }>();
    contracts.forEach((c) => {
      const b = c.unit?.building;
      if (!b) return;
      const key = String(b.id);
      if (!buildings.has(key)) {
        buildings.set(key, {
          key,
          label: `${b.id} - ${(b as any).company?.name ?? "Onbekend"}`,
        });
      }
    });
    const buildingOptions = Array.from(buildings.values());
    const base = INDEXATION_FILTER_SECTIONS;

    return [
      {
        key: BUILDING_FILTER_KEY,
        label: "Gebouw",
        options: buildingOptions,
        selectedValues: buildingFilters,
        onUpdate: setBuildingFilters,
        color: "moss" as const,
      },
      {
        key: TIME_RANGE_FILTER_KEY,
        label: "Periode",
        options: base.find((s) => s.key === TIME_RANGE_FILTER_KEY)?.options ?? [],
        selectedValues: timeRangeFilter,
        onUpdate: setTimeRangeFilter,
        color: "yellow" as const,
      },
      {
        key: INDEXATION_STATUS_FILTER_KEY,
        label: "Indexatie status",
        options: base.find((s) => s.key === INDEXATION_STATUS_FILTER_KEY)?.options ?? [],
        selectedValues: indexationStatusFilter,
        onUpdate: setIndexationStatusFilter,
        color: "brick" as const,
      },
    ];
  }, [contracts, buildingFilters, timeRangeFilter, indexationStatusFilter]);

  // ─── FilterBar chips (including kpiFilter chip) ─────────────────────────────
  const filterBarChips: FilterChip[] = useMemo(() => {
    const chips: FilterChip[] = [];

    buildingFilters.forEach((id) => {
      const label =
        filterBarSections.find((s) => s.key === BUILDING_FILTER_KEY)
          ?.options.find((o) => o.key === id)?.label ?? id;
      chips.push({
        key: `building-${id}`,
        label,
        category: "Gebouw",
        color: "moss",
        onRemove: () => setBuildingFilters((prev) => prev.filter((v) => v !== id)),
      });
    });

    timeRangeFilter.forEach((val) => {
      const label =
        INDEXATION_FILTER_SECTIONS.find((s) => s.key === TIME_RANGE_FILTER_KEY)
          ?.options.find((o) => o.key === val)?.label ?? val;
      chips.push({
        key: `timeRange-${val}`,
        label,
        category: "Periode",
        color: "yellow",
        onRemove: () => setTimeRangeFilter((prev) => prev.filter((v) => v !== val)),
      });
    });

    indexationStatusFilter.forEach((val) => {
      const label =
        INDEXATION_FILTER_SECTIONS.find((s) => s.key === INDEXATION_STATUS_FILTER_KEY)
          ?.options.find((o) => o.key === val)?.label ?? val;
      chips.push({
        key: `indexationStatus-${val}`,
        label,
        category: "Status",
        color: "brick",
        onRemove: () => setIndexationStatusFilter((prev) => prev.filter((v) => v !== val)),
      });
    });

    if (kpiFilter) {
      chips.push({
        key: "kpiFilter",
        label: kpiFilter === "thisMonth" ? "Verjaardag deze maand" : "Verjaardag volgende maand",
        category: "Periode",
        color: "moss",
        onRemove: () => setKpiFilter(null),
      });
    }

    return chips;
  }, [buildingFilters, timeRangeFilter, indexationStatusFilter, filterBarSections, kpiFilter]);

  // ─── Loading state ──────────────────────────────────────────────────────────
  const hasActiveFilters =
    !!searchText ||
    buildingFilters.length > 0 ||
    timeRangeFilter.length > 0 ||
    indexationStatusFilter.length > 0 ||
    !!kpiFilter;

  const customLoadingState = useMemo(
    () => (
      <div className="flex flex-col items-center justify-center gap-4 py-20">
        <img src={EllaAvatar} alt="Ella" className="h-16 w-16 animate-pulse rounded-full" />
        <p className="text-neutral-primary text-base font-medium">
          {hasActiveFilters
            ? "Ella zoekt de juiste contracten voor je..."
            : "Ella zoekt contracten..."}
        </p>
        <p className="text-neutral-secondary-mid text-sm">Een moment geduld</p>
      </div>
    ),
    [hasActiveFilters],
  );

  const handleTabChange = useCallback((value: string) => {
    setActiveTab(value as IndexationActiveTab);
  }, []);

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-0">
      {/* KPI bar */}
      <div className="px-4 pt-4 pb-3">
        <IndexationKPIBar
          kpis={kpis}
          duplicateUnits={duplicateUnits}
          isLoading={isLoading}
          activeKpiFilter={kpiFilter}
          onKpiFilterToggle={handleKpiFilterToggle}
        />
      </div>

      {/* Page title */}
      <div className="px-4 pb-2">
        <h2 className="text-2xl font-bold text-neutral-primary">
          Indexaties overzicht
        </h2>
      </div>

      {/* Prominent tabs */}
      <div className="px-4 pb-2">
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="h-11 gap-1 bg-muted p-1">
            <TabsTrigger
              value="never"
              className="h-9 px-5 text-sm font-medium text-neutral-secondary-mid data-[state=active]:bg-brick-primary data-[state=active]:text-white data-[state=active]:shadow-none flex items-center gap-2"
            >
              Nog nooit geïndexeerd
              {counts.never > 0 && (
                <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-white/20 px-1.5 text-xs font-semibold">
                  {counts.never}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="pending"
              className="h-9 px-5 text-sm font-medium text-neutral-secondary-mid data-[state=active]:bg-brick-primary data-[state=active]:text-white data-[state=active]:shadow-none flex items-center gap-2"
            >
              Nog te indexeren
              {counts.pending > 0 && (
                <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-white/20 px-1.5 text-xs font-semibold">
                  {counts.pending}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="indexed"
              className="h-9 px-5 text-sm font-medium text-neutral-secondary-mid data-[state=active]:bg-moss-primary data-[state=active]:text-white data-[state=active]:shadow-none flex items-center gap-2"
            >
              Reeds geïndexeerd
              {counts.indexed > 0 && (
                <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-white/20 px-1.5 text-xs font-semibold">
                  {counts.indexed}
                </span>
              )}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Table — title/description lifted to page level above */}
      <UniversalTable
        columnsConfig={INDEXATION_COLUMNS_CONFIG}
        tableId="indexation-contracts-v4"
        rows={contracts}
        title=""
        groupBy={groupBy}
        isLoading={isLoading}
        customLoadingState={customLoadingState}
        renderRow={renderRow}
      />

      {/* Spacer for fixed FilterBar */}
      <div className="h-24" />

      {/* FilterBar with pagination */}
      <FilterBar
        searchValue={searchText}
        onSearchChange={setSearchText}
        searchPlaceholder="Zoeken op unit, gebouw of huurder..."
        filterChips={filterBarChips}
        filterSections={filterBarSections}
        groupByOptions={INDEXATION_GROUPBY_OPTIONS.map((o) => ({ key: o.key, label: o.label }))}
        selectedGroupBy={groupBy}
        onGroupByChange={setGroupBy}
        showWhenEmpty={true}
        showSpacer={false}
        rightContent={
          <TablePagination
            page={page}
            pageSize={PAGE_SIZE}
            totalCount={total}
            onPageChange={setPage}
            isLoading={isLoading}
          />
        }
      />

      {/* Indexation calculation — opens in a modal on this page (no new tab) */}
      <IndexationCalculationModal
        isOpen={isCalcModalOpen}
        onClose={handleCloseCalculation}
        contract={calcContract}
        onApplied={handleIndexationApplied}
      />
    </div>
  );
};
